import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { VirtualUserPoolSchemas, VirtualUserSchemas } from "../ea.schema";
import { CountResponseSchema } from "../ea.schema.endpoints";
import {
  GetRequestBaseResponseSchema,
  VirtualUserPoolFilterSchema,
  VirtualUsersFilterSchema,
} from "../ea.schema.query";
import { VIRTUAL_USER_STATUS } from "../ea.enums";

const base = "virtual-users";
const vu = createEndpointBase(base);
const pool = createEndpointBase(base, "pool");

export const virtualUsersEndpoints = {
  virtualUsersPoolGet: pool({
    method: "GET",
    path: "",
    request: z.object({ filter: VirtualUserPoolFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(VirtualUserPoolSchemas["general"]),
    description: "Retrieves a list of virtual user pools with filtering support.",
  }),
  virtualUsersPoolCount: pool({
    method: "GET",
    path: "count",
    request: z
      .object({ filter: VirtualUserPoolFilterSchema.pick({ where: true }).optional() })
      .optional(),
    response: CountResponseSchema,
    description: "Returns the total number of virtual user pools matching the filters.",
  }),
  virtualUsersPoolCreate: pool({
    method: "POST",
    path: "",
    request: VirtualUserPoolSchemas["createData"],
    response: VirtualUserPoolSchemas["general"],
    description: "Creates a new virtual user pool.",
  }),
  virtualUsersPoolUpdate: pool({
    method: "PATCH",
    path: ":id",
    request: VirtualUserPoolSchemas["update"],
    response: VirtualUserPoolSchemas["general"],
    description: "Updates an existing virtual user pool.",
  }),
  virtualUsersPoolDelete: pool({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
    description: "Permanently deletes a virtual user pool.",
  }),

  virtualUsersGet: vu({
    method: "GET",
    path: "",
    request: z.object({ filter: VirtualUsersFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(VirtualUserSchemas.general),
    description: "Retrieves a list of virtual users with filtering support.",
  }),
  virtualUsersCount: vu({
    method: "GET",
    path: "count",
    request: z
      .object({ filter: VirtualUsersFilterSchema.pick({ where: true }).optional() })
      .optional(),
    response: CountResponseSchema,
    description: "Returns the total number of virtual users matching the filters.",
  }),
  virtualUsersCreate: vu({
    method: "POST",
    path: "",
    request: VirtualUserSchemas["createData"],
    response: VirtualUserSchemas["general"],
    description: "Creates a new virtual user.",
  }),
  virtualUsersUpdate: vu({
    method: "PATCH",
    path: ":id",
    request: VirtualUserSchemas["update"],
    response: VirtualUserSchemas["general"],
    description: "Updates an existing virtual user.",
  }),
  virtualUsersDelete: vu({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
    description: "Permanently deletes a virtual user.",
  }),
  virtualUsersDeleteScreenshots: vu({
    method: "DELETE",
    path: "screenshots",
    request: z.object({
      ids: z.array(z.number()).optional(),
    }),
    response: z.object({
      count: z.number(),
      virtualUsersCount: z.number(),
    }),
    description: "Permanently deletes error screenshots for virtual users.",
  }),
  virtualUsersToken: vu({
    method: "POST",
    path: "token",
    request: z.object({ id: z.number(), tokens: z.looseObject({}).catchall(z.any()) }),
    response: z.any(),
    description: "Manages tokens for a virtual user.",
  }),
  virtualUsersAuthenticate: vu({
    method: "POST",
    path: "authenticate",
    request: z.object({
      id: z.number(),
      extension: z.boolean().optional(),
      sensitiveData: z.array(z.object({ key: z.string(), value: z.string() })).default([]),
    }),
    response: z.object({
      url: z.string().optional(),
      sessionId: z.string().optional(),
      errorMessage: z.string().optional(),
      status: z.nativeEnum(VIRTUAL_USER_STATUS),
    }),
    description: "Authenticates a virtual user for execution.",
  }),
} as const;
